import { useTranslation } from "react-i18next";import React from 'react';
import { useTenant } from '@/shared/contexts/TenantContext';
import {
  Building2, ChevronDown, Check, Globe, LayoutGrid, Building, Warehouse, Store,
  Search, ArrowLeft, ChevronRight, Package } from
'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuGroup,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
  DropdownMenuSubContent } from
'@/shared/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription } from
'@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';


// Org node type icons
const getOrgIcon = (type) => {
  switch (type) {
    case 'SITE':
      return <Warehouse className="h-4 w-4" />;
    case 'BUSINESS_UNIT':
      return <Building className="h-4 w-4" />;
    case 'DEPT':
      return <LayoutGrid className="h-4 w-4" />;
    case 'LEGAL_ENTITY':
      return <Store className="h-4 w-4" />;
    default:
      return <Building2 className="h-4 w-4" />;
  }
};

// Hierarchical Context Switcher for header
export const BusinessSwitcher = () => {const { t } = useTranslation("common");
  const {
    availableScopes,
    activeContext,
    selectedScopes,
    selectedScopeSummary,
    toggleTenantSelection,
    toggleBusinessSelection,
    selectAllAuthorizedScopes,
    clearSelectedScopes,
    isPlatformAdmin
  } = useTenant();

  const selectedTenantIds = React.useMemo(() => new Set(selectedScopes?.selectedClientIds || []), [selectedScopes]);
  const selectedBusinessIds = React.useMemo(() => new Set(selectedScopes?.selectedBusinessIds || []), [selectedScopes]);
  const selectedCount = (selectedScopes?.selectedClientIds?.length || 0) + (selectedScopes?.selectedBusinessIds?.length || 0);
  const isTenantSelected = (tenantId) => selectedTenantIds.has(Number(tenantId));
  const isBusinessSelected = (businessId) => selectedBusinessIds.has(Number(businessId));

  // Don't show if context not loaded or no scopes available
  if (!activeContext || !availableScopes?.tenants?.length) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex items-center gap-2 h-10 px-3 hover:bg-muted/50 transition-colors border border-transparent hover:border-border rounded-lg"
          data-testid="context-switcher">
          
          <div className="p-1.5 rounded-md bg-primary/10 text-primary">
            <Globe className="h-4.5 w-4.5" />
          </div>
          <div className="flex flex-col items-start overflow-hidden text-left">
            <span className="text-sm font-semibold leading-tight truncate max-w-[120px]">
              {selectedScopeSummary || activeContext.client_name || 'Select Tenant'}
            </span>
            {selectedCount > 1 ?
            <span className="text-[10px] text-muted-foreground leading-tight uppercase tracking-wider font-bold">
                MULTI-SCOPE
              </span> :
            activeContext.business_name &&
            <span className="text-[10px] text-muted-foreground leading-tight uppercase tracking-wider font-bold">
                {activeContext.business_name}
              </span>
            }
          </div>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-72 p-1">
        <DropdownMenuLabel className="px-2 py-1.5 text-xs font-bold text-muted-foreground uppercase tracking-widest">{t("availableScopes", "Available Scopes")}

        </DropdownMenuLabel>
        
        <DropdownMenuSeparator className="my-1" />
        
        <DropdownMenuGroup>
          {availableScopes.tenants.map((tenant) =>
          <DropdownMenuSub key={tenant.id}>
              <DropdownMenuSubTrigger
              className={`flex items-center justify-between px-2 py-2 rounded-md ${
              isTenantSelected(tenant.id) ? 'bg-primary/5 text-primary' : ''}`
              }
              onClick={(event) => {
                event.preventDefault();
                toggleTenantSelection(tenant.id);
              }}
            >
                <div className="flex items-center gap-2.5">
                  <span className={`flex h-4 w-4 items-center justify-center rounded border ${
                    isTenantSelected(tenant.id) ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40'
                  }`}>
                    {isTenantSelected(tenant.id) ? <Check className="h-3 w-3" /> : <Globe className="h-3 w-3" />}
                  </span>
                  <div className="flex flex-col">
                    <span className="font-medium text-sm">{tenant.name}</span>
                    <span className="text-[10px] opacity-70">{tenant.code}</span>
                  </div>
                </div>
                {isTenantSelected(tenant.id) &&
              <Check className="h-3.5 w-3.5" />
              }
              </DropdownMenuSubTrigger>
              
              <DropdownMenuPortal>
                <DropdownMenuSubContent className="w-64 ml-1">
                  <DropdownMenuLabel className="text-[10px] font-bold text-muted-foreground uppercase px-2 py-1">{t("organizationNodes", "Organization Nodes")}

                </DropdownMenuLabel>
                  
                  {tenant.org_nodes?.length > 0 ?
                tenant.org_nodes.map((org) =>
                <DropdownMenuCheckboxItem
                  key={org.id}
                  checked={isBusinessSelected(org.id)}
                  onCheckedChange={() => toggleBusinessSelection(org.id)}
                  onSelect={(event) => event.preventDefault()}
                  className={`flex items-center justify-between px-2 py-2 cursor-pointer ${
                  isBusinessSelected(org.id) ? 'bg-primary/10 text-primary font-semibold' : ''}`
                  }>
                  
                        <div className="flex items-center gap-2.5">
                          {getOrgIcon(org.type)}
                          <div className="flex flex-col">
                            <span className="text-sm">{org.name}</span>
                            <span className="text-[10px] opacity-60">{org.type}</span>
                          </div>
                        </div>
                        {isBusinessSelected(org.id) &&
                  <Check className="h-3.5 w-3.5" />
                  }
                      </DropdownMenuCheckboxItem>
                ) :

                <div className="px-2 py-3 text-center text-xs text-muted-foreground italic">{t("noOrgNodes", "No org nodes assigned")}

                </div>
                }
                  
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                  onClick={(event) => {
                    event.preventDefault();
                    toggleTenantSelection(tenant.id);
                  }}
                  className="flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground cursor-pointer">
                  
                    <Globe className="h-3 w-3" />{isTenantSelected(tenant.id) ? 'Remove Tenant Level' : t("resetToTenant", "Select Tenant Level")}

                </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
          )}
        </DropdownMenuGroup>

        {isPlatformAdmin &&
        <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="px-2 py-1.5 text-[10px] font-bold text-accent uppercase tracking-widest" aria-hidden="true">{t("platformAdmin", "Platform Admin")}

          </DropdownMenuLabel>
            <DropdownMenuItem className="px-2 py-1.5 cursor-pointer text-xs">{t("manageAllTenants", "Manage All Tenants...")}

          </DropdownMenuItem>
            <DropdownMenuCheckboxItem
              checked={Boolean(selectedScopes?.includeAllAuthorized)}
              onCheckedChange={selectAllAuthorizedScopes}
              onSelect={(event) => event.preventDefault()}
              className="px-2 py-1.5 cursor-pointer text-xs"
            >
              Select All Authorized Scopes
            </DropdownMenuCheckboxItem>
          </>
        }
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={(event) => {
            event.preventDefault();
            clearSelectedScopes();
          }}
          className="px-2 py-1.5 cursor-pointer text-xs text-muted-foreground"
        >
          Clear to Active Scope
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>);

};

// Full-screen Premium Context Selector (shown after login)
export const BusinessSelectorModal = () => {const { t } = useTranslation("common");
  const {
    availableScopes,
    showSelector,
    setShowSelector,
    switchContext,
    activeContext
  } = useTenant();
  
  const location = require('react-router-dom').useLocation();

  const [step, setStep] = React.useState('company'); // 'company' | 'unit'
  const [selectedTenant, setSelectedTenant] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState('');

  const filteredTenants = React.useMemo(() => {
    if (!availableScopes?.tenants) return [];
    return availableScopes.tenants.filter((t) =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.code.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [availableScopes, searchQuery]);

  const filteredUnits = React.useMemo(() => {
    if (!selectedTenant?.org_nodes) return [];
    return selectedTenant.org_nodes.filter((u) =>
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.type.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [selectedTenant, searchQuery]);

  const handleTenantSelect = (tenant) => {
    if (tenant.org_nodes?.length === 1) {
      // Auto-select if only one unit
      switchContext(tenant.id, tenant.org_nodes[0].id);
      setShowSelector(false);
    } else if (tenant.org_nodes?.length > 1) {
      setSelectedTenant(tenant);
      setStep('unit');
      setSearchQuery('');
    } else {
      // Just tenant-level access
      switchContext(tenant.id);
      setShowSelector(false);
    }
  };

  const handleUnitSelect = (unit) => {
    switchContext(selectedTenant.id, unit.id);
    setShowSelector(false);
  };

  const resetSelection = () => {
    setStep('company');
    setSelectedTenant(null);
    setSearchQuery('');
  };

  if (!showSelector) return null;

  // Prevent modal from completely overlaying the public landing pages and preview pages
  const isPublicRoute = ['/', '/landing', '/landing-preview'].includes(location.pathname);
  if (isPublicRoute) return null;

  return (
    <Dialog open={showSelector} onOpenChange={setShowSelector}>
      <DialogContent className="sm:max-w-[700px] p-0 overflow-hidden bg-background border-border text-foreground shadow-2xl backdrop-blur-xl">
        <DialogHeader className="p-6 bg-gradient-to-br from-accent/5 via-background to-background border-b border-border/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-2xl bg-gradient-to-br from-accent to-accent/80 shadow-lg shadow-accent/10">
                <Package className="h-6 w-6 text-accent-foreground" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold tracking-tight font-['Space_Grotesk'] italic leading-none">
                  {step === 'company' ? 'Select Company' : `Select Unit: ${selectedTenant?.name}`}
                </DialogTitle>
                <DialogDescription className="text-muted-foreground text-[10px] font-bold tracking-widest uppercase mt-1">
                  {step === 'company' ?
                  'Choose the authorized company context to begin your session' :
                  'Drill down into a specific operational unit'}
                </DialogDescription>
              </div>
            </div>
            {step === 'unit' &&
            <Button
              variant="ghost"
              size="sm"
              onClick={resetSelection}
              className="text-muted-foreground hover:text-foreground font-bold">
              
                  <ArrowLeft className="h-4 w-4 mr-2" />{t("backToCompanies", "Back to Companies")}

            </Button>
            }
          </div>
          
          <div className="mt-6 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={step === 'company' ? "Search companies by name or code..." : "Search units..."}
              className="pl-10 bg-card border-border text-foreground placeholder:text-muted-foreground focus-visible:ring-accent/50 h-12 rounded-xl"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus />
            
          </div>
        </DialogHeader>

        <div className="max-h-[450px] overflow-y-auto p-4 custom-scrollbar bg-background/50">
          <AnimatePresence mode="wait">
            {step === 'company' ?
            <motion.div
              key="company-grid"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              
                {filteredTenants.map((tenant) =>
              <Card
                key={tenant.id}
                onClick={() => handleTenantSelect(tenant)}
                className="group cursor-pointer bg-card border-border hover:bg-accent/5 hover:border-accent/40 transition-all duration-300 overflow-hidden relative shadow-sm">
                
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-xl bg-muted border border-border flex items-center justify-center text-muted-foreground group-hover:bg-accent group-hover:text-accent-foreground transition-all duration-300 shadow-sm">
                          <Globe className="h-6 w-6" />
                        </div>
                        <div>
                          <div className="font-bold text-foreground group-hover:text-accent transition-colors">{tenant.name}</div>
                          <div className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest">{tenant.code}</div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Badge variant="outline" className="bg-muted border-border text-muted-foreground font-bold text-[10px]">
                          {tenant.org_nodes?.length || 0}{t("units", "UNITS")}
                    </Badge>
                        <ChevronRight className="h-4 w-4 text-muted group-hover:text-accent transition-transform group-hover:translate-x-1" />
                      </div>
                    </CardContent>
                  </Card>
              )}
              </motion.div> :

            <motion.div
              key="unit-grid"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-2">
              
                {filteredUnits.map((unit) =>
              <Card
                key={unit.id}
                onClick={() => handleUnitSelect(unit)}
                className="group cursor-pointer bg-card border-border hover:bg-accent/5 hover:border-accent/40 transition-all duration-200 shadow-sm">
                
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-lg bg-muted border border-border flex items-center justify-center text-muted-foreground group-hover:text-accent transition-colors">
                          {getOrgIcon(unit.type)}
                        </div>
                        <div className="flex flex-col">
                          <div className="font-semibold text-foreground group-hover:text-accent transition-colors">{unit.name}</div>
                          <div className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground group-hover:text-accent/70">{unit.type}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {activeContext?.business_id === unit.id &&
                    <Badge className="bg-accent/20 text-accent border-accent/20 font-bold text-[10px]">{t("selected", "SELECTED")}</Badge>
                    }
                        <Check className="h-5 w-5 text-accent opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </CardContent>
                  </Card>
              )}
              </motion.div>
            }
          </AnimatePresence>
          
          {(step === 'company' && filteredTenants.length === 0 || step === 'unit' && filteredUnits.length === 0) &&
          <div className="py-20 text-center">
              <div className="text-muted-foreground italic text-sm">{t("noMatchesFound", "No matches found for \"")}{searchQuery}"</div>
              <Button variant="link" onClick={() => setSearchQuery('')} className="mt-2 text-accent font-bold">{t("clearSearch", "Clear search")}</Button>
            </div>
          }
        </div>

        <div className="p-4 bg-muted/30 border-t border-border flex items-center justify-between">
          <div className="text-[10px] text-muted-foreground uppercase font-bold tracking-tight">{t("ezerpEnterpriseSecurity", "EzERP Enterprise Security Layer \u2022 Auth Verified")}

          </div>
          <div className="flex items-center gap-1.5 grayscale opacity-50">
             <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
             <span className="text-[10px] text-muted-foreground font-bold">{t("environmentProductionlocked", "Environment: PRODUCTION_LOCKED")}</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>);

};

export default BusinessSwitcher;
